// The facility's shift clock.
//
// The windows are no longer hardcoded — they come from the tenant's shift
// schedule, which is effective-dated and editable by a nurse manager. This
// module stays PURE and SYNCHRONOUS: every function takes the schedule as an
// argument. useShiftSchedule() in hooks.js supplies it, falling back to
// DEFAULT_SCHEDULE while loading or on error, so no screen has to block on
// config and a config outage can never blank out a nurse's patient list.
//
// A schedule is a list of { code, label, start_time: "HH:MM",
// end_time: "HH:MM", sort_order }. end_time is EXCLUSIVE, and a shift WRAPS
// past midnight when end_time <= start_time. Note the edge: a shift ending at
// exactly 00:00 also satisfies that test, but its tail is empty (no time is
// < 00:00), so it behaves correctly as a shift ending at midnight rather than
// one crossing it.
//
// A shift's rosterDate is the IST date it STARTS. Between midnight and the end
// of a wrapping shift, the shift in progress still belongs to YESTERDAY's
// roster date. Every consumer depends on that rule.
//
// Everything is resolved against IST, never the workstation clock — a ward PC
// with a wrong timezone must not put a nurse on the wrong shift. Mirrors
// main/ipd/shift_schedule.py + shift_window.py — change both or neither.

export const IST_TIME_ZONE = "Asia/Kolkata";

// The clock that was hardcoded before this became configurable. Used while the
// real schedule loads, and whenever the tenant has none.
export const DEFAULT_SCHEDULE = [
  { code: "morning", label: "Morning", start_time: "06:00", end_time: "14:00", sort_order: 1 },
  { code: "evening", label: "Evening", start_time: "14:00", end_time: "22:00", sort_order: 2 },
  { code: "night", label: "Night", start_time: "22:00", end_time: "06:00", sort_order: 3 },
];

const MINUTES_PER_DAY = 24 * 60;

const mins = (hhmm) => {
  const [h, m] = String(hhmm).split(":").map(Number);
  return h * 60 + m;
};
const wraps = (s) => mins(s.end_time) <= mins(s.start_time);

// JavaScript's % is REMAINDER, not modulo: (360 - 1320) % 1440 is -960 here
// where Python gives 480. Every shift wrapping midnight has a negative raw
// delta, so the result must be normalised or durations come out negative and
// the 24h total check silently fails. The Python mirror in shift_schedule.py
// needs no such correction.
const mod = (n, m) => ((n % m) + m) % m;
const durationMinutes = (s) =>
  mod(mins(s.end_time) - mins(s.start_time), MINUTES_PER_DAY) || MINUTES_PER_DAY;

const usable = (schedule) => (schedule && schedule.length ? schedule : DEFAULT_SCHEDULE);

function istParts(date) {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TIME_ZONE,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  }).formatToParts(date);
  const out = {};
  parts.forEach((p) => { out[p.type] = p.value; });
  // en-GB renders midnight as "24" in some ICU versions; normalise it.
  if (out.hour === "24") out.hour = "00";
  return out;
}

// Calendar arithmetic on the "YYYY-MM-DD" string via a UTC Date, so it can
// never be nudged by the local zone the way new Date(y, m, d) can.
function addDays(ymd, delta) {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta);
  return dt.toISOString().slice(0, 10);
}

// The shift whose window contains `t` on ITS OWN start date — a non-wrapping
// shift covering t, or a wrapping shift's head.
function shiftContaining(schedule, t) {
  return schedule.find((s) => (wraps(s)
    ? t >= mins(s.start_time)
    : t >= mins(s.start_time) && t < mins(s.end_time))) || null;
}

// A wrapping shift whose TAIL contains `t` — it started yesterday and is still
// running.
function wrappingTailShift(schedule, t) {
  return schedule.find((s) => wraps(s) && t < mins(s.end_time)) || null;
}

/**
 * The shift in progress at `now` under `schedule`, as { rosterDate, shift }.
 * rosterDate is the IST date the shift STARTED.
 */
export function currentShift(now = new Date(), schedule = DEFAULT_SCHEDULE) {
  const list = usable(schedule);
  const p = istParts(now);
  const ymd = `${p.year}-${p.month}-${p.day}`;
  const t = Number(p.hour) * 60 + Number(p.minute);

  const tail = wrappingTailShift(list, t);
  if (tail) return { rosterDate: addDays(ymd, -1), shift: tail.code };

  const s = shiftContaining(list, t);
  if (s) return { rosterDate: ymd, shift: s.code };

  // Unreachable while tiling is enforced on write; never leave a caller
  // without a shift.
  return { rosterDate: ymd, shift: list[0].code };
}

/**
 * Is this bed assignment in force at (rosterDate, shift)? The client-side
 * mirror of the backend's in_force_assignments() — same three conditions, so a
 * manager's workload chip counts what the nurse actually sees rather than every
 * pin ever made. Rows arrive already filtered on is_active by the endpoint.
 *
 * A NULL shift matches any shift (legacy rows, spec D7). A row with no window
 * at all is NOT in force: that shape only exists on a tenant where
 * 001_nurse_assignment_validity.sql has not been applied, and counting it would
 * silently reinstate the unbounded-coverage bug on exactly that tenant.
 *
 * Dates are "YYYY-MM-DD", where lexicographic order IS chronological order.
 */
export function isPinInForce(a, rosterDate, shift) {
  if (!a || !a.valid_from || !a.valid_to) return false;
  if (a.valid_from > rosterDate || a.valid_to < rosterDate) return false;
  return !a.shift || a.shift === shift;
}

/**
 * Does this pin still have a shift ahead of it — in force now, or later? The
 * manager's bed list shows these and only these: an expired pin covers nobody
 * and plans nothing, yet the list kept every one of them (QA 2026-09-18).
 *
 * On the pin's last day, a shift is still ahead when it starts no earlier than
 * the shift in progress. The day's order is by start time, because rosterDate
 * is the date a shift STARTS: a night shift beginning 22:00 is its date's last.
 * An unknown shift code cannot be placed, so it is kept rather than hidden.
 */
export function isPinCurrentOrUpcoming(a, rosterDate, shift, schedule = DEFAULT_SCHEDULE) {
  if (!a || !a.valid_from || !a.valid_to) return false;
  if (a.valid_to !== rosterDate) return a.valid_to > rosterDate;
  if (!a.shift) return true;
  const list = usable(schedule);
  const pinShift = list.find((s) => s.code === a.shift);
  const nowShift = list.find((s) => s.code === shift);
  if (!pinShift || !nowShift) return true;
  return mins(pinShift.start_time) >= mins(nowShift.start_time);
}

/**
 * Milliseconds until the shift in progress next changes. Always > 0, so a
 * timer armed with it cannot spin.
 */
export function msUntilNextShiftChange(now = new Date(), schedule = DEFAULT_SCHEDULE) {
  const list = usable(schedule);
  const p = istParts(now);
  const secondsNow = Number(p.hour) * 3600 + Number(p.minute) * 60 + Number(p.second);
  // Every shift start is a boundary; midnight is included so the timer re-arms
  // daily even on a schedule where no shift starts at 00:00.
  const boundaries = Array.from(
    new Set(list.map((s) => mins(s.start_time) * 60).concat([MINUTES_PER_DAY * 60]))
  ).sort((a, b) => a - b);
  const next = boundaries.find((b) => b > secondsNow);
  return (next - secondsNow) * 1000;
}

/** Display label for a shift code, falling back to the code itself. */
export function shiftLabel(code, schedule = DEFAULT_SCHEDULE) {
  const found = (schedule || []).find((s) => s.code === code);
  return found ? found.label : code;
}

/**
 * Null if these shifts tile the 24-hour day exactly, else an error message.
 * The editor calls this on every change so Publish is only reachable from a
 * valid state; the backend re-checks it, since the client is not trusted.
 */
export function validateTiling(shifts) {
  if (!shifts || shifts.length === 0) return "Add at least one shift.";
  const codes = shifts.map((s) => s.code);
  if (new Set(codes).size !== codes.length) return "Shift codes must be unique.";
  const ordered = [...shifts].sort((a, b) => mins(a.start_time) - mins(b.start_time));
  for (let i = 0; i < ordered.length; i += 1) {
    const s = ordered[i];
    const next = ordered[(i + 1) % ordered.length];
    if (mins(s.end_time) !== mins(next.start_time)) {
      return `"${s.code}" ends at ${s.end_time} but "${next.code}" starts at ${next.start_time} — shifts must cover the day with no gaps or overlaps.`;
    }
  }
  const total = ordered.reduce((sum, s) => sum + durationMinutes(s), 0);
  if (total !== MINUTES_PER_DAY) {
    return `Shifts total ${Math.floor(total / 60)}h ${total % 60}m; they must cover exactly 24h.`;
  }
  return null;
}
